import { motion } from "framer-motion";
import { Technology } from "@/data/modules/technologies";
import Link from "next/link";
import { useEffect, useState } from "react";

interface TechnologiesSectionProps {
  slug: string;
  delay?: number;
}

export default function TechnologiesSection({ slug, delay = 0 }: TechnologiesSectionProps) {
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [standardLibraries, setStandardLibraries] = useState<string[]>([]);

  useEffect(() => {
    async function loadTechnologies() {
      try {
        // Für den Dateinamen Punkte durch Unterstriche ersetzen
        const filename = slug.replace(/\./g, '_');
        // Dynamischer Import der case-spezifischen Technologien
        const techModule = await import(`@/data/modules/work/cases/technologies/${filename}`);
        setTechnologies(techModule.technologies || []);
        setStandardLibraries(techModule.standardLibraries || []);
      } catch (error) {
        console.error(`Fehler beim Laden der Technologien für ${slug}:`, error);
        // Fallback zur allgemeinen technologies.ts bei Fehler
        const fallbackModule = await import("@/data/modules/technologies");
        setTechnologies(fallbackModule.technologies || []);
        setStandardLibraries(fallbackModule.standardLibraries || []);
      }
    }

    loadTechnologies();
  }, [slug]);

  if (technologies.length === 0) {
    return null; // Render nichts, wenn keine Technologien geladen wurden
  }

  return (
    <motion.section
      className="mb-12"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay }}
      data-cy="technologies"
    >
      <motion.h2
        className="text-2xl md:text-3xl font-bold mb-6 text-[#ffcc88]"
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: delay + 0.2 }}
      >
        Verwendete Technologien
      </motion.h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {technologies.map((tech, index) => (
          <motion.div
            key={tech.name}
            className="bg-[#1E0B32] rounded-lg p-4 border border-[rgba(255,204,136,0.15)] shadow-glow-orange-sm"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: delay + index * 0.1 }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">{tech.name}</h3>
                <p className="text-white/80 text-sm">{tech.description}</p>
              </div>
              <Link
                href={tech.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#ffcc88] hover:text-white transition-colors"
                aria-label={`${tech.name} auf GitHub öffnen`}
              >
                <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <title>GitHub Icon</title>
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
              </Link>
            </div>
            <div className="mt-2">
              <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-[#ffcc88]/10 text-[#ffcc88]">
                {tech.category}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      {standardLibraries.length > 0 && (
        <motion.div
          className="mt-8 bg-[#1E0B32] rounded-lg p-4 border border-[rgba(255,204,136,0.15)] shadow-glow-orange-sm"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: delay + 0.2 + technologies.length * 0.1 }}
        >
          <h3 className="text-lg font-semibold text-white mb-3">Standard-Bibliotheken</h3>
          <ul className="space-y-1">
            {standardLibraries.map((lib, index) => (
              <li key={index} className="text-white/80 text-sm">
                {lib}
              </li>
            ))}
          </ul>
        </motion.div>
      )}
    </motion.section>
  );
} 